import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { Toaster, toast } from 'sonner';
import { useSelector } from 'react-redux';
import emailjs from '@emailjs/browser';
import { FD_STEP1_COLORS, FD_STEP1_SPACING } from './fdLayout';
import { FD_LOGO_WHITE_SRC,fdLogoWhiteClass } from "./fdTypography";
import {
  extractCapsuleSection,
  expandComparableMarketItems,
  repairParsedCapsule,
} from "./capsuleResponseParsers";
import {
  buildCapsuleParamsKey,
  loadLegacyProductBreakdown,
  loadProductBreakdown,
  loadQuestionnaireAnswers,
  loadMarketAnalysisSections,
  saveMarketAnalysisSections,
} from "../utils/capsuleStorage";
/**
 * Testing only: skips Market Analysis subscription check (same path as localhost).
 * Set to `false` before any production deploy that should enforce Tier 2.
 */
const TEMP_BYPASS_MARKET_ACCESS_FOR_TESTING = true;

function toTitleCaseWords(value) {
  return (value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatBrandProductTitle(brand, productType) {
  const productLabel = toTitleCaseWords(productType || "Product");
  const brandName = (brand || "").trim();
  if (brandName) return `${toTitleCaseWords(brandName)} ${productLabel}`.trim();
  return productLabel;
}

function stripMdLight(s) {
  if (!s || typeof s !== "string") return "";
  return s.replace(/\*\*/g, "").trim();
}

function parseLabelValueLines(text) {
  const rows = [];
  for (const line of stripMdLight(text)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)) {
    const cleaned = line.replace(/^[-*•\d.]+\s*/, "");
    const m = cleaned.match(/^([^:]{2,92}):\s*(.+)$/);
    if (m) {
      const val = m[2].replace(/^:\s*/, "").trim();
      rows.push({ label: m[1].trim(), value: val });
    }
  }
  return rows;
}

const LEAD_NOTE_HEADING_RE =
  /^(?:#{1,4}\s*)?(?:Key factors?|Rush\s+(?:production|options?)|Seasonal\s+considerations?|Rush\s+time|Key\s+considerations?)\b/i;

function splitLeadTimeSections(markdownText) {
  if (!markdownText?.trim()) return { main: "", summary: "" };
  const rawLines = markdownText.split(/\r?\n/);
  let cut = rawLines.length;
  for (let i = 0; i < rawLines.length; i++) {
    const L = stripMdLight(rawLines[i]);
    if (LEAD_NOTE_HEADING_RE.test(L)) {
      cut = i;
      break;
    }
  }
  if (cut === rawLines.length) return { main: markdownText.trim(), summary: "" };
  return {
    main: rawLines.slice(0, cut).join("\n").trim(),
    summary: rawLines.slice(cut).join("\n").trim(),
  };
}

function leadRowWeekWeight(value) {
  const v = stripMdLight(value || "");
  const wr = v.match(/(\d+)\s*[-–]\s*(\d+)\s*weeks?/i);
  const wo = v.match(/(\d+)\s*weeks?/i);
  if (wr) return (parseInt(wr[1], 10) + parseInt(wr[2], 10)) / 2;
  if (wo) return parseInt(wo[1], 10);
  return 12;
}

const LEAD_TIME_NOTES = [
  {
    label: "Key factors",
    value: "Complexity of style detail, quality checks.",
  },
  {
    label: "Rush production",
    value: "Possible with increased costs.",
  },
  {
    label: "Seasonal considerations",
    value: "Avoid Chinese New Year, plan around holiday rushes.",
  },
];

const LEAD_TIME_DOMESTIC = {
  header: { label: "Domestic Timeline", value: "12–16 weeks" },
  steps: [
    { label: "Sourcing", value: "1–2 weeks" },
    { label: "Patterns", value: "2 weeks" },
    { label: "Samples", value: "2–3 weeks" },
    { label: "Pre-Production", value: "1 week" },
    { label: "Production", value: "6–8 weeks" },
  ],
};

const LEAD_TIME_OVERSEAS = {
  header: { label: "Overseas Timeline", value: "10–17 weeks" },
  steps: [
    { label: "Sourcing", value: "1–2 weeks" },
    { label: "Patterns", value: "2 weeks" },
    { label: "Samples", value: "2–3 weeks" },
    { label: "Overseas Samples", value: "1–2 weeks" },
    { label: "Production", value: "4–8 weeks" },
  ],
};

function getLeadTimeRegion(manufacturingPreference = {}) {
  const usa = !!manufacturingPreference.usa;
  const intl = !!manufacturingPreference.international;
  if (intl && !usa) return "overseas";
  if (usa && !intl) return "domestic";
  if (intl) return "overseas";
  return "domestic";
}

function finalizeLeadRowsWithPct(temp) {
  if (!temp.length) return [];
  const subRows = temp.slice(1);
  const subTotal = subRows.reduce((sum, row) => sum + row.w, 0) || 1;
  return temp.map((r, index) => ({
    label: r.label,
    value: r.value,
    pct:
      index === 0
        ? 100
        : Math.min(100, Math.max(8, Math.round((r.w / subTotal) * 100))),
  }));
}

function buildLeadTimelineRows(region) {
  const template = region === "overseas" ? LEAD_TIME_OVERSEAS : LEAD_TIME_DOMESTIC;
  const temp = [
    {
      label: template.header.label,
      value: template.header.value,
      w: leadRowWeekWeight(template.header.value),
    },
    ...template.steps.map((step) => ({
      label: step.label,
      value: step.value,
      w: leadRowWeekWeight(step.value),
    })),
  ];
  return finalizeLeadRowsWithPct(temp);
}

function resolveLeadSummaryRows(aiSummaryText) {
  const parsed = parseLeadSummaryLines(aiSummaryText || "");
  if (parsed.length >= 2) return parsed;
  return LEAD_TIME_NOTES;
}

/** Short cue for timeline row header (week range sits above filled bar). */
function extractLeadWeekCue(rawValue) {
  const v = stripMdLight(rawValue || "");
  const range = v.match(/(\d+\s*[-–]\s*\d+)\s*(?:weeks?|wk\.?)?/i);
  if (range) return `${range[1].replace(/\s*-\s*/, "–")} weeks`;
  const wo = v.match(/(\d+)\s*(?:weeks?|wk\.?)/i);
  if (wo) return `${wo[1]} weeks`;
  return "";
}


/** White rounded card (mockup: discrete left-column panels). */
function MarketWhiteCard({ title, children }) {
  return (
    <section className="rounded-[22px] sm:rounded-[26px] bg-white border border-black/[0.07] shadow-[0_10px_36px_rgba(0,0,0,0.055)] px-5 py-6 sm:px-7 sm:py-8">
      <h3 className="font-heading text-[1.3125rem] sm:text-xl text-[#1E1D1B] tracking-tight leading-snug mb-5 sm:mb-6">
        {title}
      </h3>
      {children}
    </section>
  );
}

/** Cream column for Lead Time (mockup: tall right rail). */
function MarketLeadTimeShell({ children }) {
  return (
    <div className="rounded-[26px] sm:rounded-[30px] bg-[#E8E6E1] border border-[#D4D0C8] shadow-[0_16px_44px_rgba(0,0,0,0.08)] px-5 py-7 sm:px-7 sm:py-9">
      {children}
    </div>
  );
}

/** PDF ref: full-width hairline + thick dark segment duration bar. */
function LeadDurationBar({ pct }) {
  const w = Math.min(100, Math.max(5, pct ?? 14));
  return (
    <div className="relative mt-4 mb-1 w-full h-[6px] shrink-0">
      <div className="absolute inset-x-0 bottom-0 h-px bg-[#C4BDB4]" aria-hidden />
      <div
        className="absolute left-0 bottom-0 h-[5px] rounded-[1px] bg-[#231f1f]"
        style={{ width: `${w}%`, maxWidth: "100%" }}
        aria-hidden
      />
    </div>
  );
}

function parseLeadSummaryLines(text) {
  const rows = [];
  const lines = stripMdLight(text)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  let pendingLabel = null;
  const pendingValues = [];

  const flush = () => {
    if (pendingLabel && pendingValues.length) {
      rows.push({ label: pendingLabel, value: pendingValues.join(" ") });
    }
    pendingLabel = null;
    pendingValues.length = 0;
  };

  for (const line of lines) {
    const cleaned = line.replace(/^[-*•]\s*/, "").replace(/^#{1,4}\s*/, "");
    // "Label: value on same line"
    const mInline = cleaned.match(/^([^:]{2,80}):\s*(.+)$/);
    // "Label:" heading only (value on next line(s))
    const mHeading = !mInline && cleaned.match(/^([^:]{2,80}):\s*$/);

    if (mInline) {
      flush();
      const val = mInline[2].replace(/^:\s*/, "").trim();
      rows.push({ label: mInline[1].trim(), value: val });
    } else if (mHeading) {
      flush();
      pendingLabel = mHeading[1].trim();
    } else if (pendingLabel) {
      pendingValues.push(cleaned);
    }
  }
  flush();

  return rows.filter((r) => r.value);
}

function parseComparableBrandsList(text) {
  const expanded = expandComparableMarketItems(text);
  if (expanded.length) return expanded;
  const t = stripMdLight(text);
  const lines = t.split(/\r?\n/).map((l) => l.replace(/^[-*•\d.]+\s*/, "").trim()).filter(Boolean);
  if (lines.length >= 1) return lines;
  const comma = t.split(/(?:,|•|\/)\s*/).map((s) => s.trim()).filter(Boolean);
  return comma.length ? comma : t ? [t] : [];
}

function parseConsumerInsightTiles(text) {
  const tiles = [];
  const lines = stripMdLight(text)
    .split(/\r?\n/)
    .map((l) => l.replace(/^[-*•\d.]+\s*/, "").trim())
    .filter(Boolean);
  for (const line of lines) {
    const m = line.match(
      /^(Age\s*range|Lifestyle|Values|Buying\s*motivations?|Psychographics)\s*:?\s*(.+)$/i
    );
    if (!m) continue;
    const label = m[1].trim();
    const normalized =
      /^buying/i.test(label)
        ? "Buying motivations"
        : /^age/i.test(label)
          ? "Age Range"
          : /^lifestyle|^psychographics/i.test(label)
            ? "Lifestyle"
            : "Values";
    const val = m[2].replace(/^:\s*/, "").trim();
    tiles.push({ key: normalized, value: val });
  }
  return tiles;
}

/** Pull "18–35" style spans from prose (e.g. "Targeting adults aged 18–35 who…"). */
function extractAgeSpanFromProse(fullText) {
  const s = stripMdLight(fullText).replace(/\s+/g, " ");
  let m =
    s.match(/\badults?\s+aged\s+(\d{1,2})\s*[-–]\s*(\d{1,3})\b/i) ||
    s.match(/\baged\s+(\d{1,2})\s*[-–]\s*(\d{1,3})\b/i) ||
    s.match(/\b(?:age\s*range)\s*[:\s]+(\d{1,2})\s*[-–]\s*(\d{1,3})\b/i);
  if (m) return `${m[1]}–${m[2]}`;
  m = s.match(/\b(\d{1,2})\s+(?:to|through)\s+(\d{1,3})\s+years?\b/i);
  if (m) return `${m[1]}–${m[2]}`;
  return null;
}

/** Fills Age / Lifestyle etc. when the model returned a paragraph instead of four lines. */
function buildAudienceSlotsByKey(rawInsight, parsedOrdered) {
  const keys = ["Age Range", "Lifestyle", "Values", "Buying motivations"];
  const map = {};
  for (const t of parsedOrdered) {
    if (t.value) map[t.key] = t.value;
  }
  const flat = stripMdLight(rawInsight || "").trim();
  const prose = flat.replace(/\s*\n+\s*/g, " ").trim();

  if (!map["Age Range"] && prose) {
    const g = extractAgeSpanFromProse(prose);
    if (g) map["Age Range"] = g;
  }

  const parsedFilled = parsedOrdered.filter((t) => (t.value || "").trim()).length;

  if (!map["Lifestyle"] && prose && parsedFilled < 2) {
    map["Lifestyle"] = prose.length > 420 ? `${prose.slice(0, 417)}…` : prose;
  }

  const out = {};
  for (const k of keys) {
    const v = (map[k] || "").trim();
    out[k] = v;
  }
  return out;
}

function orderConsumerInsightTiles(tiles) {
  const order = ["Age Range", "Lifestyle", "Values", "Buying motivations"];
  const used = new Set();
  const out = [];
  for (const key of order) {
    const hit = tiles.find((t) => t.key === key);
    if (hit) {
      out.push(hit);
      used.add(hit);
    }
  }
  for (const t of tiles) {
    if (!used.has(t)) out.push(t);
  }
  return out;
}

function sanitizePctHighlight(str) {
  if (!str || typeof str !== "string") return "";
  return str.replace(/\uFEFF|[\u200B-\u200D\u2060]/g, "").trim();
}

/** Optional "(note)" after a dollar range only — keeps margin formulas intact. */
function splitFinancialValueAndSub(fullVal) {
  const val = stripMdLight(fullVal).trim();
  if (!val) return { mainVal: "", sub: "" };
  if (/=/.test(val) && /[()]/.test(val)) return { mainVal: val, sub: "" };
  const tailNote = val.match(/^(.+)\s+\(([^)]+)\)\s*$/);
  if (!tailNote) return { mainVal: val, sub: "" };
  let mainVal = tailNote[1].trim();
  const sub = tailNote[2].trim();
  if (mainVal.includes("(") || /^[\s(]+$/.test(mainVal)) return { mainVal: val, sub: "" };
  if (mainVal.length > 72) return { mainVal: val, sub: "" };
  return { mainVal, sub };
}

/** Models often return all financial rows on one line; split before known labels so `Label: value` parsing works. */
function injectNewlinesBeforeFinancialLabels(text, kind) {
  let s = stripMdLight(text || "").trim();
  if (!s) return s;
  // Models often glue labeled rows on one line; split before known labels.
  s = s.replace(/(\])(\s*)(?=Gross\s+margin)/i, "$1\n");
  s = s.replace(
    /(\$[\d,.]+(?:\s*[-–]\s*\$[\d,.]+)?)(\s*)(?=Production\s+cost)/gi,
    "$1\n"
  );
  s = s.replace(
    /(%|=\s*[\d.]+%)(\s*)(?=Wholesale\s+(?:gross\s+margin|price\s+range))/gi,
    "$1\n"
  );
  s = s.replace(
    /(%|=\s*[\d.]+%)(\s*)(?=DTC\s+gross\s+margin)/gi,
    "$1\n"
  );
  const labelishLines = (s.match(/^\s*[^:\n]{2,96}:\s*\S.*/gm) || []).length;
  if (labelishLines >= 3) return s;
  const re =
    kind === "pricing"
      ? /\s+(?=(?:Wholesale gross margin percentage|DTC gross margin percentage|Retail\s*\/\s*DTC\s+price\s+range|Wholesale price range)\s*:)/gi
      : /\s+(?=(?:Gross margin percentage|Production cost per unit(?:\s*\([^)]+\))?\s*|Production cost(?:\s+per unit)?\s*|Retail price)\s*:)/gi;
  return s.replace(re, "\n");
}

function parseFinancialDarkCard(text) {
  const raw = stripMdLight(text);
  let highlight =
    raw.match(/=\s*([\d.]+%)/)?.[1] ||
    raw.match(/\b([\d.]+%)\s*$/m)?.[1] ||
    "";
  highlight = sanitizePctHighlight(highlight);
  const lines = [];
  for (const part of raw.split(/\n+/).map((l) => l.trim()).filter(Boolean)) {
    const t = stripMdLight(part).replace(/^[-*•\d.]+\s*/, "");
    const m = t.match(/^([^:]+):\s*(.+)$/);
    if (!m) continue;
    const label = m[1].trim();
    if (label.length < 2 || label.length > 96) continue;
    const rawVal = m[2].trim();
    const { mainVal, sub } = splitFinancialValueAndSub(rawVal);
    lines.push({ label, value: mainVal, sub });
  }
  if (!highlight && lines.length) {
    const joined = lines.map((l) => l.value).join("\n");
    const fromRows =
      joined.match(/=\s*([\d.]+%)/)?.[1] ||
      joined.match(/\b([\d.]+%)\b/)?.[1] ||
      "";
    highlight = sanitizePctHighlight(fromRows);
  }
  return { lines, highlight };
}

function MarketMdBody({ children }) {
  return (
    <div className="text-[13px] sm:text-[14px] font-normal leading-relaxed text-[#232220] [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:list-none [&_ul]:pl-0 [&_li]:mb-2 [&_strong]:font-normal">
      <ReactMarkdown
        components={{
          hr: () => null,
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}

const DEMO_TILE_SHELLS = [
  "rounded-[14px] sm:rounded-2xl border border-[#D5D2CA] bg-[#EBEBE8] px-5 py-6 sm:px-7 sm:py-8",
  "rounded-[14px] sm:rounded-2xl border border-[#DAD6CF] bg-[#E7E6E2] px-5 py-6 sm:px-7 sm:py-8",
  "rounded-[14px] sm:rounded-2xl border border-[#D8D6D0] bg-[#F0EFEC] px-5 py-6 sm:px-7 sm:py-8",
  "rounded-[14px] sm:rounded-2xl border border-[#D9D8D2] bg-[#E4E3DD] px-5 py-6 sm:px-7 sm:py-8",
];

function DemographicMutedCard({ label, children, toneIndex = 0 }) {
  const shell = DEMO_TILE_SHELLS[toneIndex % DEMO_TILE_SHELLS.length];
  return (
    <div
      className={`flex min-h-[10rem] sm:min-h-[10.75rem] flex-col justify-center ${shell} text-center shadow-none`}
    >
      <span className="font-sans text-[13px] font-medium text-[#292724]">
        {label}
      </span>
      <div className="mt-4 text-[#1E1D1B]">{children}</div>
    </div>
  );
}

/** PDF ref: Comparable card + fixed 2×2 audience dashboard (four small muted tiles). */
function AudienceDashboardFourGrid({ audienceByKey }) {
  const keys = ["Age Range", "Lifestyle", "Values", "Buying motivations"];
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4">
      {keys.map((key, idx) => {
        const raw = (audienceByKey[key] || "").trim();
        return (
          <DemographicMutedCard
            key={key}
            label={`${key}:`}
            toneIndex={idx}
          >
            {key === "Age Range" ? (
              raw ? (
                <span className="block font-sans text-[clamp(2.35rem,7.5vw,3.85rem)] font-extralight tabular-nums leading-[0.98] tracking-[-0.02em] text-[#1a1816]">
                  {raw}
                </span>
              ) : (
                <span className="font-sans text-[13px] text-[#b0aba2]" aria-hidden>
                  —
                </span>
              )
            ) : raw ? (
              <p className="mx-auto max-w-[18rem] font-sans text-[13px] sm:text-[14px] leading-snug text-[#2a2723]">
                {raw}
              </p>
            ) : (
              <span className="font-sans text-[13px] text-[#b0aba2]" aria-hidden>
                —
              </span>
            )}
          </DemographicMutedCard>
        );
      })}
    </div>
  );
}

function DarkFinancialCard({ title, parsed, fallbackText, showHighlight }) {
  const { lines, highlight } = parsed;
  const showHL = Boolean(showHighlight && highlight && lines.length > 0);
  const pct = sanitizePctHighlight(highlight);

  /** Single tone for Margin + Wholesale cards (dark olive-brown, not navy). */
  const shell =
    "rounded-[28px] sm:rounded-[30px] bg-[#2C2C26] px-7 sm:px-9 py-8 sm:py-10 text-white shadow-[0_24px_58px_rgba(20,19,14,0.26)] ring-1 ring-[#faf9f5]/10";

  return (
    <div className={shell}>
      <h3 className="font-heading text-[clamp(1.45rem,3.8vw,1.8rem)] font-normal tracking-tight text-white mb-8 sm:mb-9">
        {title}
      </h3>
      {lines.length > 0 ? (
        <>
          {lines.map((row, i) => (
            <div
              key={`${row.label}-${i}`}
              className={i > 0 ? "mt-5 pt-5 border-t border-[#484844]/90" : ""}
            >
              <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 items-baseline font-sans">
                <span className="max-w-[52%] text-[10px] sm:text-[11px] font-normal uppercase tracking-[0.2em] text-[#C9C5BE] leading-relaxed">
                  {row.label}:
                </span>
                <span className="text-right text-[14px] sm:text-[15px] font-normal not-italic text-white tabular-nums">
                  {row.value}
                </span>
              </div>
              {row.sub ? (
                <p className="mt-2 text-right text-[11px] sm:text-[12px] font-normal text-[#A8A49C] not-italic tracking-tight">
                  ({row.sub})
                </p>
              ) : null}
            </div>
          ))}
          {showHL ? (
            <div className="mt-8 pt-2 text-right font-heading text-[clamp(1.25rem,3.6vw,1.85rem)] font-light not-italic text-white tracking-tight tabular-nums">
              {`= ${pct}`}
            </div>
          ) : null}
        </>
      ) : (
        <div className="text-white/92 text-sm font-normal [&_p]:mb-3 [&_strong]:font-normal">
          <ReactMarkdown
            components={{
              hr: () => null,
            }}
          >
            {fallbackText || "No data available."}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}

export default function Step4bMarketFinancials({ onBack, onRestart, outputSessionKey, runKey: runKeyProp }) {
  const activeRunKey = runKeyProp || outputSessionKey;
  const formData = useSelector((state) => state.form);
  const savedAnswers = useMemo(
    () => loadQuestionnaireAnswers(formData, activeRunKey),
    [formData, activeRunKey]
  );
  const [sendingEmail, setSendingEmail] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [sections, setSections] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [missingCustomerId, setMissingCustomerId] = useState(false);

  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocalhost =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "0.0.0.0";

  /** Dev / preview only: set REACT_APP_BYPASS_MARKET_ANALYSIS_ACCESS=true at build time. Never enable on the public production storefront. */
  const bypassMarketPaywall =
    process.env.REACT_APP_BYPASS_MARKET_ANALYSIS_ACCESS === "true";

  const { localBrand, brand2, productType, manufacturingPreference } = formData;

  const title = formatBrandProductTitle(localBrand || brand2, productType);

  const paramsKey = useMemo(
    () => buildCapsuleParamsKey(formData, savedAnswers, activeRunKey),
    [formData, savedAnswers, activeRunKey]
  );

  const cachedSections = useMemo(
    () => loadMarketAnalysisSections(paramsKey),
    [paramsKey]
  );

  const saveSectionsToCache = useCallback(
    (nextSections) => {
      saveMarketAnalysisSections(paramsKey, nextSections);
    },
    [paramsKey]
  );

  // Reset sections whenever the run key changes so stale data from a previous
  // run is never displayed while waiting for the new run's breakdown.
  const prevParamsKeyRef = useRef(null);
  useLayoutEffect(() => {
    if (prevParamsKeyRef.current !== null && prevParamsKeyRef.current !== paramsKey) {
      setSections(null);
    }
    prevParamsKeyRef.current = paramsKey;
  }, [paramsKey]);

  const displaySections = sections ?? cachedSections;

  // Remove trailing dashes and similar AI artifacts from section text
  const sanitizeSectionText = (text) => {
    if (!text || typeof text !== 'string') return text;
    let s = text.trim();
    
    // Remove the triple em dash separator (⸻) and similar characters
    s = s.replace(/⸻/g, '');
    
    // Remove all types of dashes (em dash —, en dash –, hyphen -, and similar characters)
    // Remove trailing lines that are only dashes, underscores, or whitespace
    s = s.replace(/\n[\s\-–—_⸻]*$/g, '');
    
    // Remove trailing dashes (all types) with optional whitespace at end of each line
    s = s.replace(/[\s\-–—⸻]+$/gm, '');
    
    // Remove trailing dash/hyphen/underscore at end of last line (after all line breaks)
    s = s.replace(/[\s\-–—_⸻]+$/, '');
    
    // Remove any standalone dash lines (lines that are only dashes or separators)
    s = s.replace(/^[\s\-–—_⸻]+\n?$/gm, '');
    
    // Remove lines containing only the separator character with optional whitespace
    s = s.replace(/^\s*⸻\s*$/gm, '');
    
    // Final trim
    return s.trim();
  };

  const buildMarketSections = useCallback((rawText, parsedSuggestions) => {
    const safeParsed =
      parsedSuggestions && typeof parsedSuggestions === "object"
        ? parsedSuggestions
        : {};

    const getSectionLegacy = (label) => {
      const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const patterns = [
        new RegExp(
          `\\*\\*${escaped}\\*\\*\\s*:?\\s*\\n*([\\s\\S]*?)(?=\\n\\s*\\*\\*|\\n⸻|$)`,
          "i"
        ),
        new RegExp(
          `\\*\\*${escaped}\\*\\*\\s*:?\\s*\\n([\\s\\S]*?)(?=\\n\\*\\*|\\n⸻|$)`,
          "i"
        ),
        new RegExp(
          `\\*\\*${escaped}\\*\\*\\s*([\\s\\S]*?)(?=\\n\\s*\\*\\*|\\n⸻|$)`,
          "i"
        ),
        new RegExp(
          `${escaped}\\s*:?\\s*\\n([\\s\\S]*?)(?=\\n\\s*\\*\\*|\\n⸻|$)`,
          "i"
        ),
      ];
      for (const pattern of patterns) {
        const match = rawText.match(pattern);
        if (match && match[1] && match[1].trim()) {
          return match[1].trim();
        }
      }
      return "";
    };

    const merge = (parsedKey, boundaryKey, startLabels) => {
      const fromParsed = safeParsed[parsedKey];
      if (typeof fromParsed === "string" && fromParsed.trim()) {
        return sanitizeSectionText(fromParsed);
      }
      const extracted = extractCapsuleSection(
        rawText || "",
        startLabels,
        boundaryKey
      );
      if (extracted.trim()) {
        return sanitizeSectionText(extracted);
      }
      for (const lab of startLabels) {
        const leg = getSectionLegacy(lab);
        if (leg.trim()) return sanitizeSectionText(leg);
      }
      return "";
    };

    return {
      materials: merge("materials", "Materials", ["Materials"]),
      companionItems: merge(
        "companionItems",
        "Companion Items",
        ["Companion Items"]
      ),
      saleprices: merge("saleprices", "Sales Price", ["Sales Price"]),
      marketExamples: merge(
        "marketExamples",
        "Comparable Market Examples",
        ["Comparable Market Examples"]
      ),
      targetInsight: merge(
        "targetInsight",
        "Target Consumer Insight",
        [
          "Target Consumer Insight",
          "Target Consumer Insights",
          "Target Consumer",
          "Target Audience",
          "Target Audience Insight",
          "Target Audience Insights",
          "Consumer Insight",
          "Consumer Insights",
          "Target Consumer Profile",
          "Target Consumer Profiles",
        ]
      ),
      marginAnalysis: merge(
        "marginAnalysis",
        "Margin Analysis",
        ["Margin Analysis"]
      ),
      pricing: merge(
        "pricing",
        "Wholesale vs. DTC Pricing",
        [
          "Wholesale vs. DTC Pricing",
          "Wholesale vs DTC Pricing",
          "Wholesale vs DTC",
          "Wholesale vs Direct-to-Consumer Pricing",
          "Wholesale vs. Direct-to-Consumer (DTC) Pricing",
          "Wholesale and DTC Pricing",
          "DTC and Wholesale Pricing",
        ]
      ),
      yieldConsumption: merge(
        "yieldConsumption",
        "Yield & Consumption Estimates",
        [
          "Yield & Consumption Estimates",
          "Yield and Consumption Estimates",
          "Yield & Consumption",
          "Yield and Consumption",
          "Fabric Yield & Consumption Estimates",
          "Fabric Yield and Consumption Estimates",
        ]
      ),
      leadTime: merge(
        "leadTime",
        "Production Lead Time Estimate",
        ["Production Lead Time Estimate", "Production Lead Time", "Lead Time Estimate", "Lead Time"]
      ),
    };
  }, []);

  const readCurrentBreakdown = useCallback(() => {
    // Only load breakdowns that are keyed to the current paramsKey.
    // The legacy fallback (loadLatestProductBreakdown) is intentionally
    // omitted here — it reads unscoped localStorage keys and would return
    // data from the previous run, causing the stale-output bug.
    const fromHashed =
      loadProductBreakdown(paramsKey, null) ||
      loadLegacyProductBreakdown(paramsKey);
    if (fromHashed) {
      return {
        rawText: fromHashed.rawAnswer,
        parsed: fromHashed.parsedSuggestions,
      };
    }
    return null;
  }, [paramsKey]);

  /** Re-merge financial / analysis sections from canonical keys for this run. */
  const lsMergedSections = useMemo(() => {
    const breakdown = readCurrentBreakdown();
    if (!breakdown) return null;
    return buildMarketSections(breakdown.rawText, repairParsedCapsule(breakdown.parsed, breakdown.rawText));
  }, [buildMarketSections, readCurrentBreakdown]);

  // Check page access on mount (no hard redirect — use in-app states)
  useEffect(() => {
    const checkAccess = async () => {
      const params = new URLSearchParams(window.location.search);
      const cid = params.get("customer_id");

      if (
        isLocalhost ||
        bypassMarketPaywall ||
        TEMP_BYPASS_MARKET_ACCESS_FOR_TESTING
      ) {
        setHasAccess(true);
        setAccessChecked(true);
        setAccessDenied(false);
        setMissingCustomerId(false);
        return;
      }

      if (!cid) {
        setMissingCustomerId(true);
        setHasAccess(false);
        setAccessDenied(false);
        setAccessChecked(true);
        return;
      }

      try {
        const response = await fetch(
          `https://backend-capsule-builder.onrender.com/proxy/check-page-access?customer_id=${cid}&page=market-analysis`
        );
        const data = await response.json();

        if (data.ok && data.allowed) {
          setHasAccess(true);
          setAccessDenied(false);
          setMissingCustomerId(false);
        } else {
          setAccessDenied(true);
          setHasAccess(false);
          setMissingCustomerId(false);
        }
      } catch (err) {
        console.error("Access check error:", err);
        setAccessDenied(true);
        setHasAccess(false);
        setMissingCustomerId(false);
      }
      setAccessChecked(true);
    };

    checkAccess();
  }, [isLocalhost, bypassMarketPaywall]);

  // Load sections from the current run's product breakdown (once per paramsKey).
  // If the breakdown isn't saved yet (Step4Suggestions is still fetching),
  // retry every 800 ms until it appears rather than falling back to stale data.
  const loadedParamsRef = useRef(null);
  useEffect(() => {
    if (!hasAccess) return;
    if (loadedParamsRef.current === paramsKey && sections) return;

    // Use cached market-analysis sections when available for this exact key.
    if (cachedSections) {
      setSections(cachedSections);
      loadedParamsRef.current = paramsKey;
      return;
    }

    const tryLoad = () => {
      const breakdown = readCurrentBreakdown();
      if (!breakdown) {
        // The current run's breakdown hasn't been saved yet by Step4Suggestions.
        // Return null and let the polling interval retry.
        return false;
      }
      const repaired = repairParsedCapsule(breakdown.parsed, breakdown.rawText);
      const built = buildMarketSections(breakdown.rawText, repaired);
      setSections(built);
      loadedParamsRef.current = paramsKey;
      if (
        built.materials ||
        built.companionItems ||
        built.saleprices ||
        built.marketExamples ||
        built.targetInsight ||
        built.marginAnalysis ||
        built.pricing ||
        built.yieldConsumption ||
        built.leadTime
      ) {
        saveSectionsToCache(built);
      }
      return true;
    };

    // Attempt immediately; if Step4 hasn't saved the breakdown yet, poll.
    if (tryLoad()) return;

    const intervalId = setInterval(() => {
      if (tryLoad()) {
        clearInterval(intervalId);
      }
    }, 800);

    return () => clearInterval(intervalId);
  }, [
    hasAccess,
    paramsKey,
    cachedSections,
    saveSectionsToCache,
    buildMarketSections,
    readCurrentBreakdown,
    sections,
  ]);

  /** Pull cream body up over hero (~30% overlap), same ratio as Line Strategy / input steps. */
  const marketOverlapRef = useRef(null);
  const [marketOverlapPx, setMarketOverlapPx] = useState(() =>
    Math.round(520 * FD_STEP1_SPACING.cardOverlapRatio)
  );

  useLayoutEffect(() => {
    if (!hasAccess || !displaySections) return undefined;
    const el = marketOverlapRef.current;
    if (!el) return undefined;
    const update = () => {
      const h = el.offsetHeight;
      const next = Math.round(h * FD_STEP1_SPACING.cardOverlapRatio);
      if (next > 0) setMarketOverlapPx((p) => (p === next ? p : next));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [hasAccess, displaySections]);

  // Show loading while checking access
  if (!accessChecked) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-black font-sans text-[16px] font-normal leading-[1.2]">
            Verifying access...
          </p>
        </div>
      </div>
    );
  }

  if (missingCustomerId) {
    return (
      <>
        <Toaster position="top-right" richColors />
        <div className="bg-white min-h-screen flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <h2 className="text-[28px] font-heading font-semibold text-black mb-3">
              Sign in required
            </h2>
            <p className="text-[15px] font-sans text-gray-700 mb-6 leading-relaxed">
              Open the Capsule Builder from your Form Department account so your session
              includes a customer ID. The subscription page opened before because the app
              could not verify access without it.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={onBack}
                className="px-6 py-3 text-[15px] font-semibold text-black bg-gray-200 hover:bg-gray-300 rounded-lg transition-all"
              >
                ← Back to results
              </button>
              <a
                href="https://formdepartment.com/account/login"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 text-[15px] font-semibold text-white bg-black hover:bg-[#3A3A3D] rounded-lg transition-all inline-block"
              >
                Go to account login
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show upgrade page if access is denied (tier / plan from backend)
  if (accessDenied && !hasAccess) {
    const tier2ProductId = 8424683241647;
    const tier2CheckoutUrl = `https://formdepartment.com/cart/${tier2ProductId}:1`;
    
    return (
      <>
        <Toaster position="top-right" richColors />
        <div className="bg-white min-h-screen flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <h2 className="text-[32px] font-heading font-semibold text-black mb-4">
              Upgrade Required
            </h2>
            <p className="text-[16px] font-sans text-gray-700 mb-8 leading-relaxed">
              Your current subscription plan doesn't include access to the Market Analysis page. 
              Upgrade to Tier 2 to unlock production timelines, market analysis, and more advanced features.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onBack}
                className="px-6 py-3 text-[16px] font-bold text-black bg-gray-200 hover:bg-gray-300 rounded-lg transition-all"
              >
                ← Go Back
              </button>
              <a
                href={tier2CheckoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 text-[16px] font-bold text-white bg-black hover:bg-[#3A3A3D] rounded-lg transition-all inline-block"
              >
                Buy Tier 2 →
              </a>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Show loading while loading sections
  if (!hasAccess || !displaySections) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-black font-sans text-[16px] font-normal leading-[1.2]">
            Loading Market Analysis...
          </p>
        </div>
      </div>
    );
  }

  const marketExamples =
    displaySections?.marketExamples?.trim() ||
    lsMergedSections.marketExamples?.trim() ||
    "";
  const targetInsight =
    displaySections?.targetInsight?.trim() ||
    lsMergedSections.targetInsight?.trim() ||
    "";
  const marginAnalysis =
    displaySections?.marginAnalysis?.trim() ||
    lsMergedSections.marginAnalysis?.trim() ||
    "";
  const pricingBase =
    displaySections?.pricing?.trim() || lsMergedSections.pricing?.trim() || "";
  const yieldConsumption =
    displaySections?.yieldConsumption?.trim() ||
    lsMergedSections.yieldConsumption?.trim() ||
    "";
  const leadTime =
    displaySections?.leadTime?.trim() || lsMergedSections.leadTime?.trim() || "";

  const yieldRows = parseLabelValueLines(yieldConsumption);
  const leadParts = splitLeadTimeSections(leadTime);
  const leadRegion = getLeadTimeRegion(manufacturingPreference);
  const leadRows = buildLeadTimelineRows(leadRegion);
  const leadSummaryRows = resolveLeadSummaryRows(leadParts.summary || "");
  const comparableBrands = parseComparableBrandsList(marketExamples);
  const consumerTiles = parseConsumerInsightTiles(targetInsight);
  const orderedConsumerTiles = orderConsumerInsightTiles(consumerTiles);
  const audienceByKey = buildAudienceSlotsByKey(targetInsight, orderedConsumerTiles);

  const { rawText: rawAnswerLs } = readCurrentBreakdown();

  let marginForCard = marginAnalysis;
  let marginParsed = parseFinancialDarkCard(
    injectNewlinesBeforeFinancialLabels(marginForCard, "margin")
  );
  if (marginParsed.lines.length === 0 && rawAnswerLs.length > 40) {
    const marginFromRaw =
      buildMarketSections(rawAnswerLs, {}).marginAnalysis?.trim() || "";
    if (marginFromRaw) {
      marginForCard = marginFromRaw;
      marginParsed = parseFinancialDarkCard(
        injectNewlinesBeforeFinancialLabels(marginForCard, "margin")
      );
    }
  }

  let pricingForCard = pricingBase;
  let wholesaleParsed = parseFinancialDarkCard(
    injectNewlinesBeforeFinancialLabels(pricingForCard, "pricing")
  );
  if (wholesaleParsed.lines.length === 0 && rawAnswerLs.length > 40) {
    const fromRawOnly = buildMarketSections(rawAnswerLs, {}).pricing?.trim() || "";
    if (fromRawOnly) {
      pricingForCard = fromRawOnly;
      wholesaleParsed = parseFinancialDarkCard(
        injectNewlinesBeforeFinancialLabels(pricingForCard, "pricing")
      );
    }
  }

  const marketBlurb =
    "A technical deep-dive into the construction, sourcing and economic blueprint of your performance silhouette.";

  // Build email params
  const buildEmailParams = () => {
    const recipient = process.env.REACT_APP_TEAM_RECEIVER_EMAIL;
    const customerEmail =
      savedAnswers?.email || savedAnswers?.contactEmail || savedAnswers?.userEmail || '';

    return {
      to_email: recipient,
      customer_email: customerEmail,
      title,
      brand: brand2 || localBrand || '',
      product_type: productType || '',
      raw_answer: readCurrentBreakdown().rawText,
      scheduling_url:
        process.env.REACT_APP_SCHEDULING_URL ||
        'https://app.acuityscheduling.com/schedule/c38a96dc/appointment/32120137/calendar/3784845?appointmentTypeIds[]=32120137',
    };
  };

  // Send email
  const sendScheduleEmail = async () => {
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (!serviceId || !templateId || !publicKey) throw new Error('Missing EmailJS env vars');
    const templateParams = buildEmailParams();
    return emailjs.send(serviceId, templateId, templateParams, { publicKey });
  };

  async function handleScheduleClick() {
    const schedulingUrl =
      process.env.REACT_APP_SCHEDULING_URL ||
      'https://app.acuityscheduling.com/schedule/c38a96dc/appointment/32120137/calendar/3784845?appointmentTypeIds[]=32120137';
    
    window.open(schedulingUrl, '_blank', 'noopener,noreferrer');
    
    try {
      setSendingEmail(true);
      await sendScheduleEmail();
      toast.success('Your details were emailed to our team.');
    } catch (err) {
      console.error('Email send failed:', err);
      toast.error('We could not send the email. We will still see your booking.');
    } finally {
      setSendingEmail(false);
    }
  }

  return (
    <>
      <Toaster position="top-right" richColors />
      <div
        className="min-h-screen w-full overflow-x-hidden pb-10 sm:pb-12"
        style={{ backgroundColor: FD_STEP1_COLORS.pageBottom }}
        data-capsule-step="market-analysis"
      >
        {/* Hero — overlaps upward into cream body (same pattern as Line Strategy) */}
        <section
          className="relative z-0 flex w-full min-h-[min(52vh,420px)] flex-col items-center justify-end px-4 pt-14 text-center text-white sm:min-h-[min(52vh,480px)] sm:pt-16"
          style={{
            backgroundImage:
              'linear-gradient(180deg, rgba(0,0,0,0.52) 0%, rgba(0,0,0,0.66) 100%), url("/assets/ayo-ogunseinde-UqT55tGBqzI-unsplash_dark_clean.jpg")',
            backgroundSize: "cover",
            backgroundPosition: "center 28%",
            paddingBottom: FD_STEP1_SPACING.heroTitleToCard + marketOverlapPx,
          }}
        >
          <img
            src={FD_LOGO_WHITE_SRC}
            alt="Form Department"
            className={`absolute top-6 sm:top-8 left-1/2 z-10 -translate-x-1/2 ${fdLogoWhiteClass}`}
          />

          <h1 className="relative z-10 mt-28 sm:mt-32 mb-0 pb-2 font-heading text-[clamp(1.85rem,5vw,2.75rem)] text-white tracking-tight leading-[1.1] drop-shadow-[0_2px_24px_rgba(0,0,0,0.35)] sm:pb-4">
            Market Analysis
          </h1>

          <p className="sr-only">
            Production and market positioning for your capsule concept
          </p>
        </section>

        <div
          ref={marketOverlapRef}
          className="relative z-20 px-4 sm:px-6 md:px-8 lg:px-12"
          style={{
            marginTop: marketOverlapPx ? -marketOverlapPx : undefined,
            backgroundColor: "#f5f4f1",
            borderTopLeftRadius: "28px",
            borderTopRightRadius: "28px",
            paddingTop: "1.25rem",
          }}
        >
        <div className="relative z-10 mx-auto w-full min-w-0 max-w-[1100px]">
          <section className="pb-8 sm:pb-10">
            <h2 className="font-heading text-[clamp(1.9rem,5.2vw,2.85rem)] text-[#161514] leading-[1.04] tracking-tight">
              {title}
            </h2>
            <p className="mt-5 max-w-xl text-[14px] sm:text-[15px] leading-relaxed text-[#2a2825] font-sans font-normal">
              {marketBlurb}
            </p>
          </section>

          <div className="flex flex-col gap-8 lg:gap-10">
            <div className="flex flex-col gap-8 lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(280px,360px)] lg:gap-x-10 lg:items-start">
              <div className="flex min-w-0 flex-col gap-6 lg:gap-8">
                <MarketWhiteCard title="Yield & Consumption Estimates">
                  <div className="space-y-3.5 text-[13px] sm:text-[14px] font-sans text-[#232220] leading-relaxed">
                    {yieldRows.length ? (
                      yieldRows.map((r) => (
                        <p key={`${r.label}-${r.value.slice(0, 40)}`} className="m-0 font-normal">
                          <span className="text-[#141312]">{r.label}: </span>
                          <span className="text-[#4a463f]">{r.value}</span>
                        </p>
                      ))
                    ) : yieldConsumption ? (
                      <MarketMdBody>{yieldConsumption}</MarketMdBody>
                    ) : (
                      <p className="text-sm text-[#756F68] font-sans">No data available</p>
                    )}
                  </div>
                </MarketWhiteCard>

                <MarketWhiteCard title="Comparable Market Examples">
                  {comparableBrands.length ? (
                    <div className="flex flex-col gap-3 sm:gap-3.5 font-sans text-[14px] sm:text-[15px] text-[#3a3834] text-left">
                      {comparableBrands.map((b, i) => (
                        <div key={`${i}-${b.slice(0, 24)}`}>{b}</div>
                      ))}
                    </div>
                  ) : marketExamples ? (
                    <div className="[&_.font-heading]:text-[#3a3834]">
                      <MarketMdBody>{marketExamples}</MarketMdBody>
                    </div>
                  ) : (
                    <p className="text-sm text-[#756F68] font-sans">No data available</p>
                  )}
                </MarketWhiteCard>

                <section>
                  <h3 className="mb-5 font-heading text-[1.3125rem] sm:text-xl md:text-[1.375rem] text-[#1E1D1B] tracking-tight leading-snug sm:mb-6">
                    Target Audience
                  </h3>
                  <AudienceDashboardFourGrid audienceByKey={audienceByKey} />
                </section>
              </div>

              <aside className="min-w-0 w-full lg:sticky lg:top-8 lg:self-start">
                <MarketLeadTimeShell>
                  <h3 className="mb-7 font-heading text-[1.625rem] font-medium leading-tight tracking-tight text-[#1E1D1B] sm:mb-9 sm:text-[1.875rem] md:text-[2rem]">
                    Lead Time
                  </h3>
                  {leadRows.length ? (
                    <div className="space-y-0">
                      {leadRows.map((r, i) => {
                        const cue = extractLeadWeekCue(r.value);
                        const isHeader = i === 0;
                        return (
                          <div
                            key={`${r.label}-${i}`}
                            className={
                              isHeader
                                ? "pb-6 sm:pb-7 border-b border-[#C4BDB4]/60 mb-1"
                                : "pb-5 sm:pb-6 last:pb-2"
                            }
                          >
                            <div className="flex items-baseline justify-between gap-2">
                              <p
                                className={`m-0 font-sans text-[#1a1816] min-w-0 truncate ${
                                  isHeader
                                    ? "text-[14px] sm:text-[15px] font-semibold"
                                    : "text-[13px] sm:text-[14px] font-semibold"
                                }`}
                              >
                                {r.label.replace(/:+\s*$/, "")}
                              </p>
                              {cue ? (
                                <p className="m-0 text-[12px] sm:text-[13px] font-sans font-normal text-[#4a463e] tabular-nums shrink-0">
                                  {cue}
                                </p>
                              ) : null}
                            </div>
                            <LeadDurationBar pct={r.pct} />
                          </div>
                        );
                      })}
                      {leadSummaryRows.length > 0 ? (
                        <div className="mt-6 sm:mt-8 rounded-[16px] sm:rounded-[18px] bg-[#DAD6D0] px-5 py-6 sm:px-7 sm:py-7 font-sans text-[13px] sm:text-[14px] leading-relaxed text-[#292724] space-y-3 border border-black/[0.06]">
                          {leadSummaryRows.map((row) => (
                            <p key={row.label} className="m-0">
                              <span className="font-bold text-[#1a1816]">
                                {row.label}:{" "}
                              </span>
                              <span className="font-normal">{row.value}</span>
                            </p>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : leadTime ? (
                    <div className="text-[#232220] text-[14px] sm:text-[15px] font-normal leading-relaxed whitespace-pre-wrap">
                      <MarketMdBody>{leadTime}</MarketMdBody>
                    </div>
                  ) : (
                    <p className="text-sm text-[#756F68] font-sans">No data available</p>
                  )}
                </MarketLeadTimeShell>
              </aside>
            </div>

            <section className="flex flex-col gap-4 sm:gap-5">
              <DarkFinancialCard
                title="Margin Analysis"
                parsed={marginParsed}
                fallbackText={marginForCard}
                showHighlight
              />
              <DarkFinancialCard
                title="Wholesale vs DTC Pricing"
                parsed={wholesaleParsed}
                fallbackText={pricingForCard}
                showHighlight={false}
              />
            </section>

            {onRestart ? (
              <div className="flex justify-center pt-2">
                <button
                  type="button"
                  onClick={onRestart}
                  className="inline-flex min-h-[48px] items-center justify-center rounded-full border border-[#2B2A25] px-8 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2B2A25] hover:bg-[#2B2A25] hover:text-white transition-colors"
                >
                  Build a new item
                </button>
              </div>
            ) : null}
          </div>

          {/* Footer actions */}
          <div className="mt-11 flex w-full flex-row flex-nowrap items-center justify-between gap-2 border-t border-black/[0.08] pt-9 sm:gap-6">
              <button
                type="button"
                onClick={onBack}
                className="flex min-w-0 shrink-0 touch-manipulation items-center gap-2 font-sans text-[10px] font-semibold tracking-[0.2em] text-[#282522] hover:opacity-80 active:opacity-70 transition-opacity sm:gap-3 sm:text-[11px] sm:tracking-[0.22em]"
              >
                <span className="inline-flex h-9 w-9 shrink-0 touch-manipulation items-center justify-center rounded-full border border-[#282522]/85 text-[15px] font-light leading-none sm:h-10 sm:w-10 sm:text-base">
                  ←
                </span>
                <span className="font-semibold uppercase">Back</span>
              </button>
              <button
                type="button"
                onClick={handleScheduleClick}
                disabled={sendingEmail}
                className={`flex min-h-[44px] max-w-[min(12.5rem,calc(100%-6.5rem))] shrink touch-manipulation items-center justify-center gap-2 rounded-full px-4 py-2.5 text-[9px] font-bold uppercase tracking-[0.16em] text-white transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:max-w-none sm:min-h-[46px] sm:gap-2.5 sm:px-6 sm:text-[10px] sm:tracking-[0.18em] ${
                  sendingEmail
                    ? "cursor-not-allowed bg-neutral-400"
                    : "bg-[#2D2D2A] hover:bg-[#252522] focus-visible:outline-white/40"
                }`}
              >
                <span className={sendingEmail ? "text-center whitespace-normal leading-tight" : ""}>
                  {sendingEmail ? "Sending details…" : "Schedule a call"}
                </span>
                {!sendingEmail ? (
                  <span className="text-sm leading-none shrink-0 sm:text-base" aria-hidden>
                    →
                  </span>
                ) : null}
              </button>
            </div>
        </div>
        </div>
      </div>
    </>
  );
}